/** Theme presets aligned with ytl-logistics-frontend (9 accents + blue default). */
export type Theme =
  | "blue"
  | "slate"
  | "green"
  | "purple"
  | "coral"
  | "sage"
  | "teal"
  | "orange"
  | "magenta";

export const THEME_ORDER: Theme[] = [
  "blue",
  "slate",
  "green",
  "purple",
  "coral",
  "sage",
  "teal",
  "orange",
  "magenta",
];

/** Map localStorage id cũ → preset logistics. */
export const LEGACY_THEME_MAP: Record<string, Theme> = {
  light: "blue",
  dark: "slate",
  cobalt: "blue",
  emerald: "green",
  rose: "coral",
  amber: "orange",
  violet: "purple",
  crimson: "magenta",
};

export const migrateLegacyThemeId = (raw: string): Theme | undefined =>
  Object.prototype.hasOwnProperty.call(LEGACY_THEME_MAP, raw)
    ? LEGACY_THEME_MAP[raw]
    : undefined;

export const DEFAULT_THEME: Theme = "blue";

export const themeLabels: Record<Theme, string> = {
  blue: "Xanh dương",
  slate: "Than chì",
  green: "Xanh lá",
  purple: "Tím",
  coral: "Coral",
  sage: "Sage",
  teal: "Teal",
  orange: "Cam",
  magenta: "Hồng",
};

export const themePreviewHex: Record<Theme, string> = {
  blue: "#2563eb",
  slate: "#475569",
  green: "#22c55e",
  purple: "#7c3aed",
  coral: "#f43f5e",
  sage: "#6d8b74",
  teal: "#0ea5e9",
  orange: "#ea580c",
  magenta: "#db2777",
};

export interface ThemeConfig {
  "--color-gray-dark": string;
  "--color-gray-light": string;
  "--color-blue-dark": string;
  "--color-blue-light": string;
  "--color-blue-hover": string;
  "--color-white": string;
  "--color-black": string;
  "--color-primary": string;
  "--color-disabled-bg": string;
  "--color-disabled-border": string;
  "--color-navbar-bg": string;
  "--color-header-text": string;
  "--color-content-bg": string;
  "--color-menu-normal": string;
  "--color-menu-active": string;
  "--color-menu-hover": string;
  "--color-table-header-bg": string;
  "--color-table-header-text": string;
  "--color-table-row-hover": string;
  "--color-table-border": string;
  "--color-btn-primary": string;
  "--color-btn-primary-hover": string;
  "--color-btn-danger": string;
  "--color-btn-danger-hover": string;
  "--color-btn-default": string;
  "--color-btn-default-hover": string;
  "--color-btn-success": string;
  "--color-btn-success-hover": string;
  "--color-btn-warning": string;
  "--color-btn-warning-hover": string;
  "--color-btn-info": string;
  "--color-btn-info-hover": string;
  /* Sidebar (logistics shell) */
  "--color-sidebar-bg": string;
  "--color-sidebar-border": string;
  "--color-sidebar-item": string;
  "--color-sidebar-icon": string;
  "--color-sidebar-hover-bg": string;
  "--color-sidebar-active-bg": string;
  "--color-sidebar-active-bg-hover": string;
  "--color-sidebar-active-text": string;
  "--color-sidebar-active-icon": string;
  "--color-sidebar-section-bg": string;
  "--color-sidebar-nested-bg": string;
  "--color-sidebar-nested-border": string;
  /* Form & Input — shared across light themes */
  "--form-bg": string;
  "--form-card-bg": string;
  "--form-border": string;
  "--form-border-radius": string;
  "--form-shadow": string;
  "--form-padding": string;
  "--form-spacing-sm": string;
  "--form-spacing-md": string;
  "--form-spacing-lg": string;
  "--form-section-bg": string;
  "--form-section-border": string;
  "--form-title-size": string;
  "--form-title-weight": string;
  "--form-label-size": string;
  "--form-text-color": string;
  "--form-actions-gap": string;
  "--input-bg": string;
  "--input-border": string;
  "--input-border-hover": string;
  "--input-border-focus": string;
  "--input-text-color": string;
  "--input-placeholder-color": string;
  "--input-disabled-bg": string;
  "--input-disabled-text": string;
  "--input-border-radius": string;
  "--input-padding": string;
  "--input-height": string;
}

type AccentTone = Pick<
  ThemeConfig,
  | "--color-primary"
  | "--color-navbar-bg"
  | "--color-menu-normal"
  | "--color-menu-active"
  | "--color-menu-hover"
  | "--color-table-header-bg"
  | "--color-table-header-text"
  | "--color-table-border"
  | "--color-table-row-hover"
  | "--color-btn-primary"
  | "--color-btn-primary-hover"
  | "--color-gray-dark"
  | "--color-gray-light"
  | "--color-blue-dark"
  | "--color-blue-light"
  | "--color-blue-hover"
  | "--color-sidebar-active-bg"
  | "--color-sidebar-active-bg-hover"
  | "--color-sidebar-active-text"
  | "--color-sidebar-active-icon"
  | "--color-sidebar-section-bg"
  | "--color-sidebar-nested-bg"
  | "--color-sidebar-nested-border"
  | "--input-border-focus"
>;

const FORM_TOKENS: Pick<
  ThemeConfig,
  | "--form-bg"
  | "--form-card-bg"
  | "--form-border"
  | "--form-border-radius"
  | "--form-shadow"
  | "--form-padding"
  | "--form-spacing-sm"
  | "--form-spacing-md"
  | "--form-spacing-lg"
  | "--form-section-bg"
  | "--form-section-border"
  | "--form-title-size"
  | "--form-title-weight"
  | "--form-label-size"
  | "--form-text-color"
  | "--form-actions-gap"
  | "--input-bg"
  | "--input-border"
  | "--input-border-hover"
  | "--input-text-color"
  | "--input-placeholder-color"
  | "--input-disabled-bg"
  | "--input-disabled-text"
  | "--input-border-radius"
  | "--input-padding"
  | "--input-height"
> = {
  "--form-bg": "#f8fafc",
  "--form-card-bg": "#ffffff",
  "--form-border": "#e2e8f0",
  "--form-border-radius": "8px",
  "--form-shadow": "0 1px 3px 0 rgba(15,23,42,0.08)",
  "--form-padding": "12px",
  "--form-spacing-sm": "6px",
  "--form-spacing-md": "8px",
  "--form-spacing-lg": "12px",
  "--form-section-bg": "#ffffff",
  "--form-section-border": "#e2e8f0",
  "--form-title-size": "0.6875rem",
  "--form-title-weight": "700",
  "--form-label-size": "0.8125rem",
  "--form-text-color": "#0f172a",
  "--form-actions-gap": "8px",
  "--input-bg": "#ffffff",
  "--input-border": "#d9d9d9",
  "--input-border-hover": "#bfbfbf",
  "--input-text-color": "#0f172a",
  "--input-placeholder-color": "#94a3b8",
  "--input-disabled-bg": "#f1f5f9",
  "--input-disabled-text": "#94a3b8",
  "--input-border-radius": "8px",
  "--input-padding": "6.5px 12px",
  "--input-height": "32px",
};

const accentTheme = (a: AccentTone): ThemeConfig => ({
  ...a,
  "--color-white": "#ffffff",
  "--color-black": "#0f172a",
  "--color-header-text": "#ffffff",
  "--color-content-bg": "#f8fafc",
  "--color-disabled-bg": "#e2e8f0",
  "--color-disabled-border": "#cbd5e1",
  "--color-btn-danger": "#dc2626",
  "--color-btn-danger-hover": "#f87171",
  "--color-btn-default": "#64748b",
  "--color-btn-default-hover": "#475569",
  "--color-btn-success": "#15803d",
  "--color-btn-success-hover": "#16a34a",
  "--color-btn-warning": "#d97706",
  "--color-btn-warning-hover": "#f59e0b",
  "--color-btn-info": a["--color-btn-primary"],
  "--color-btn-info-hover": a["--color-btn-primary-hover"],
  "--color-sidebar-bg": "#ffffff",
  "--color-sidebar-border": "#e2e8f0",
  "--color-sidebar-item": "#1e293b",
  "--color-sidebar-icon": "#64748b",
  "--color-sidebar-hover-bg": "#f1f5f9",
  ...FORM_TOKENS,
  "--input-border-focus": a["--input-border-focus"],
});

export const themes: Record<Theme, ThemeConfig> = {
  /** Default — khớp PALETTE_BLUE logistics */
  blue: accentTheme({
    "--color-primary": "#2563eb",
    "--color-navbar-bg": "#1e40af",
    "--color-menu-normal": "#1d4ed8",
    "--color-menu-hover": "#3b82f6",
    "--color-menu-active": "#1e3a8a",
    "--color-table-header-bg": "#b8cae8",
    "--color-table-header-text": "#0f2744",
    "--color-table-border": "#7f9bc0",
    "--color-table-row-hover": "#eef2ff",
    "--color-btn-primary": "#2563eb",
    "--color-btn-primary-hover": "#1d4ed8",
    "--color-gray-dark": "#0f172a",
    "--color-gray-light": "#3b82f6",
    "--color-blue-dark": "#1d4ed8",
    "--color-blue-light": "#eff6ff",
    "--color-blue-hover": "#3b82f6",
    "--color-sidebar-active-bg": "#eff6ff",
    "--color-sidebar-active-bg-hover": "#dbeafe",
    "--color-sidebar-active-text": "#1d4ed8",
    "--color-sidebar-active-icon": "#2563eb",
    "--color-sidebar-section-bg": "#f1f5f9",
    "--color-sidebar-nested-bg": "#eff6ff",
    "--color-sidebar-nested-border": "#bfdbfe",
    "--input-border-focus": "#2563eb",
  }),
  slate: accentTheme({
    "--color-primary": "#475569",
    "--color-navbar-bg": "#1e293b",
    "--color-menu-normal": "#334155",
    "--color-menu-hover": "#64748b",
    "--color-menu-active": "#0f172a",
    "--color-table-header-bg": "#e2e8f0",
    "--color-table-header-text": "#334155",
    "--color-table-border": "#cbd5e1",
    "--color-table-row-hover": "#f8fafc",
    "--color-btn-primary": "#475569",
    "--color-btn-primary-hover": "#334155",
    "--color-gray-dark": "#0f172a",
    "--color-gray-light": "#64748b",
    "--color-blue-dark": "#334155",
    "--color-blue-light": "#f1f5f9",
    "--color-blue-hover": "#64748b",
    "--color-sidebar-active-bg": "#f1f5f9",
    "--color-sidebar-active-bg-hover": "#e2e8f0",
    "--color-sidebar-active-text": "#334155",
    "--color-sidebar-active-icon": "#475569",
    "--color-sidebar-section-bg": "#f1f5f9",
    "--color-sidebar-nested-bg": "#f1f5f9",
    "--color-sidebar-nested-border": "#cbd5e1",
    "--input-border-focus": "#475569",
  }),
  green: accentTheme({
    "--color-primary": "#22c55e",
    "--color-navbar-bg": "#15803d",
    "--color-menu-normal": "#166534",
    "--color-menu-hover": "#4ade80",
    "--color-menu-active": "#14532d",
    "--color-table-header-bg": "#d1fae5",
    "--color-table-header-text": "#166534",
    "--color-table-border": "#a7f3d0",
    "--color-table-row-hover": "#f0fdf4",
    "--color-btn-primary": "#16a34a",
    "--color-btn-primary-hover": "#15803d",
    "--color-gray-dark": "#14532d",
    "--color-gray-light": "#22c55e",
    "--color-blue-dark": "#15803d",
    "--color-blue-light": "#ecfdf5",
    "--color-blue-hover": "#4ade80",
    "--color-sidebar-active-bg": "#ecfdf5",
    "--color-sidebar-active-bg-hover": "#d1fae5",
    "--color-sidebar-active-text": "#166534",
    "--color-sidebar-active-icon": "#22c55e",
    "--color-sidebar-section-bg": "#f0fdf4",
    "--color-sidebar-nested-bg": "#ecfdf5",
    "--color-sidebar-nested-border": "#a7f3d0",
    "--input-border-focus": "#22c55e",
  }),
  purple: accentTheme({
    "--color-primary": "#7c3aed",
    "--color-navbar-bg": "#5b21b6",
    "--color-menu-normal": "#6d28d9",
    "--color-menu-hover": "#a78bfa",
    "--color-menu-active": "#4c1d95",
    "--color-table-header-bg": "#ede9fe",
    "--color-table-header-text": "#5b21b6",
    "--color-table-border": "#ddd6fe",
    "--color-table-row-hover": "#faf5ff",
    "--color-btn-primary": "#7c3aed",
    "--color-btn-primary-hover": "#6d28d9",
    "--color-gray-dark": "#4c1d95",
    "--color-gray-light": "#7c3aed",
    "--color-blue-dark": "#5b21b6",
    "--color-blue-light": "#f5f3ff",
    "--color-blue-hover": "#a78bfa",
    "--color-sidebar-active-bg": "#f5f3ff",
    "--color-sidebar-active-bg-hover": "#ede9fe",
    "--color-sidebar-active-text": "#5b21b6",
    "--color-sidebar-active-icon": "#7c3aed",
    "--color-sidebar-section-bg": "#f5f3ff",
    "--color-sidebar-nested-bg": "#f5f3ff",
    "--color-sidebar-nested-border": "#ddd6fe",
    "--input-border-focus": "#7c3aed",
  }),
  coral: accentTheme({
    "--color-primary": "#f43f5e",
    "--color-navbar-bg": "#be123c",
    "--color-menu-normal": "#e11d48",
    "--color-menu-hover": "#fb7185",
    "--color-menu-active": "#9f1239",
    "--color-table-header-bg": "#ffe4e6",
    "--color-table-header-text": "#881337",
    "--color-table-border": "#fecdd3",
    "--color-table-row-hover": "#fff1f2",
    "--color-btn-primary": "#e11d48",
    "--color-btn-primary-hover": "#be123c",
    "--color-gray-dark": "#9f1239",
    "--color-gray-light": "#f43f5e",
    "--color-blue-dark": "#be123c",
    "--color-blue-light": "#fff1f2",
    "--color-blue-hover": "#fb7185",
    "--color-sidebar-active-bg": "#fff1f2",
    "--color-sidebar-active-bg-hover": "#ffe4e6",
    "--color-sidebar-active-text": "#be123c",
    "--color-sidebar-active-icon": "#f43f5e",
    "--color-sidebar-section-bg": "#fff1f2",
    "--color-sidebar-nested-bg": "#fff1f2",
    "--color-sidebar-nested-border": "#fecdd3",
    "--input-border-focus": "#f43f5e",
  }),
  sage: accentTheme({
    "--color-primary": "#6d8b74",
    "--color-navbar-bg": "#3d5344",
    "--color-menu-normal": "#4a6b53",
    "--color-menu-hover": "#8daa94",
    "--color-menu-active": "#354239",
    "--color-table-header-bg": "#e8f1ea",
    "--color-table-header-text": "#2f4536",
    "--color-table-border": "#c8d9cc",
    "--color-table-row-hover": "#f6faf7",
    "--color-btn-primary": "#4a6b53",
    "--color-btn-primary-hover": "#3d5344",
    "--color-gray-dark": "#354239",
    "--color-gray-light": "#6d8b74",
    "--color-blue-dark": "#4a6b53",
    "--color-blue-light": "#f3f7f4",
    "--color-blue-hover": "#8daa94",
    "--color-sidebar-active-bg": "#edf5ef",
    "--color-sidebar-active-bg-hover": "#dfece2",
    "--color-sidebar-active-text": "#3f5246",
    "--color-sidebar-active-icon": "#6d8b74",
    "--color-sidebar-section-bg": "#f3f7f4",
    "--color-sidebar-nested-bg": "#f3f7f4",
    "--color-sidebar-nested-border": "#c8d9cc",
    "--input-border-focus": "#6d8b74",
  }),
  teal: accentTheme({
    "--color-primary": "#0ea5e9",
    "--color-navbar-bg": "#0369a1",
    "--color-menu-normal": "#0284c7",
    "--color-menu-hover": "#38bdf8",
    "--color-menu-active": "#0c4a6e",
    "--color-table-header-bg": "#dbeafe",
    "--color-table-header-text": "#1e40af",
    "--color-table-border": "#93c5fd",
    "--color-table-row-hover": "#eff6ff",
    "--color-btn-primary": "#0284c7",
    "--color-btn-primary-hover": "#0369a1",
    "--color-gray-dark": "#0c4a6e",
    "--color-gray-light": "#0ea5e9",
    "--color-blue-dark": "#0369a1",
    "--color-blue-light": "#f0f9ff",
    "--color-blue-hover": "#38bdf8",
    "--color-sidebar-active-bg": "#f0f9ff",
    "--color-sidebar-active-bg-hover": "#e0f2fe",
    "--color-sidebar-active-text": "#0369a1",
    "--color-sidebar-active-icon": "#0ea5e9",
    "--color-sidebar-section-bg": "#f0f9ff",
    "--color-sidebar-nested-bg": "#f0f9ff",
    "--color-sidebar-nested-border": "#bae6fd",
    "--input-border-focus": "#0ea5e9",
  }),
  orange: accentTheme({
    "--color-primary": "#ea580c",
    "--color-navbar-bg": "#9a3412",
    "--color-menu-normal": "#c2410c",
    "--color-menu-hover": "#fb923c",
    "--color-menu-active": "#7c2d12",
    "--color-table-header-bg": "#ffedd5",
    "--color-table-header-text": "#9a3412",
    "--color-table-border": "#fed7aa",
    "--color-table-row-hover": "#fff7ed",
    "--color-btn-primary": "#ea580c",
    "--color-btn-primary-hover": "#c2410c",
    "--color-gray-dark": "#7c2d12",
    "--color-gray-light": "#ea580c",
    "--color-blue-dark": "#c2410c",
    "--color-blue-light": "#fff7ed",
    "--color-blue-hover": "#fb923c",
    "--color-sidebar-active-bg": "#fff7ed",
    "--color-sidebar-active-bg-hover": "#ffedd5",
    "--color-sidebar-active-text": "#c2410c",
    "--color-sidebar-active-icon": "#ea580c",
    "--color-sidebar-section-bg": "#fff7ed",
    "--color-sidebar-nested-bg": "#fff7ed",
    "--color-sidebar-nested-border": "#fed7aa",
    "--input-border-focus": "#ea580c",
  }),
  magenta: accentTheme({
    "--color-primary": "#db2777",
    "--color-navbar-bg": "#831843",
    "--color-menu-normal": "#be185d",
    "--color-menu-hover": "#f472b6",
    "--color-menu-active": "#9d174d",
    "--color-table-header-bg": "#fce7f3",
    "--color-table-header-text": "#831843",
    "--color-table-border": "#fbcfe8",
    "--color-table-row-hover": "#fdf2f8",
    "--color-btn-primary": "#db2777",
    "--color-btn-primary-hover": "#be185d",
    "--color-gray-dark": "#831843",
    "--color-gray-light": "#db2777",
    "--color-blue-dark": "#be185d",
    "--color-blue-light": "#fdf2f8",
    "--color-blue-hover": "#f472b6",
    "--color-sidebar-active-bg": "#fdf2f8",
    "--color-sidebar-active-bg-hover": "#fce7f3",
    "--color-sidebar-active-text": "#9d174d",
    "--color-sidebar-active-icon": "#db2777",
    "--color-sidebar-section-bg": "#fdf2f8",
    "--color-sidebar-nested-bg": "#fdf2f8",
    "--color-sidebar-nested-border": "#fbcfe8",
    "--input-border-focus": "#db2777",
  }),
};

export const buttonDisabledStyles = {
  opacity: 0.2,
};
